import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, GraduationCap, Users, Calendar,
  Award, BookOpen, ArrowRight, Shield, Zap, Star,
  Globe, Briefcase, Code, Terminal, MessageSquare
} from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { getBootcampStatus } from "../lib/bootcamp";

const BootcampSection = () => {
  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };
  const soraFont = { fontFamily: "'Sora', sans-serif" };
  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    getBootcampStatus().then(data => setBootcampStatus(data));
  }, []);

  return (
    <section style={{ backgroundColor: '#0A0F1E', ...soraFont }} className="relative py-20 md:py-32 overflow-hidden border-t border-[#1F2937]">
      {/* Background orbs */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#00E676]/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <div style={{ borderColor: '#F59E0B33', backgroundColor: '#F59E0B11' }} className="inline-flex items-center gap-2 px-3 py-1.5 border mb-8">
            <GraduationCap className="w-3.5 h-3.5 text-[#F59E0B]" />
            <span style={monoFont} className="text-[9px] font-black text-[#F59E0B] uppercase tracking-[0.3em]">Premium Bootcamp</span>
          </div>
          <h2 style={monoFont} className="text-[clamp(2.5rem,6vw,4rem)] font-black text-white mb-8 leading-[1.05] uppercase tracking-tighter">
            From Python Zero to <span className="text-[#F59E0B] italic">AI Hero</span>
          </h2>
          <p style={soraFont} className="text-[#6B7280] max-w-2xl mx-auto text-lg leading-relaxed">
            The most practical path to mastering Machine Learning. Build 12 production-grade projects and get career-ready.
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="bootcamp-grid">
          
          {/* 1. Large Pricing Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            style={{ backgroundColor: '#111827', borderColor: '#F59E0B66' }}
            className="pricing-card pos-pricing order-1 border p-10 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4">
               <div style={{ backgroundColor: '#F59E0B', color: '#0A0F1E' }} className="px-3 py-1 text-[8px] font-black uppercase tracking-widest">Best Value</div>
            </div>

            <div className="mb-10">
              <span style={monoFont} className="text-[9px] text-[#F59E0B] uppercase tracking-[0.4em] font-black block mb-4">Lifetime Access</span>
              <h3 style={monoFont} className="text-3xl font-black text-white uppercase tracking-tight">AI/ML MASTERCLASS</h3>
            </div>
            
            <div className="mb-12">
              <div className="flex items-end gap-3 mb-2">
                <span style={monoFont} className="text-6xl font-black text-white">₹4,999</span>
                <span style={monoFont} className="text-[#6B7280] text-sm line-through mb-2">₹9,999</span>
              </div>
              <span style={monoFont} className="text-[#F59E0B] text-[10px] font-black uppercase tracking-widest">Limited Time Offer · 50% Off</span>
            </div>

            <div className="space-y-6 mb-12 flex-1">
              {[
                "12 Industry Projects",
                "Live Weekly Mentorship",
                "Placement Support",
                "Verified Certificate"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 text-white text-sm font-black uppercase tracking-tight">
                  <CheckCircle2 className="w-5 h-5 text-[#F59E0B]" />
                  {item}
                </div>
              ))}
            </div>

            {bootcampStatus?.status === 'enrolling' && bootcampStatus.enrollmentDate && (
              <div style={monoFont} className="mb-8 p-4 border border-[#F59E0B22] bg-[#F59E0B05]">
                <span className="text-[#F59E0B] text-[9px] font-black uppercase tracking-[0.3em] block mb-1">Next Batch Starts</span>
                <span className="text-xl font-black text-white uppercase">
                  {format(new Date(bootcampStatus.enrollmentDate), 'MMM dd, yyyy')}
                </span>
              </div>
            )}

            <Link to="/bootcamp" className="mt-auto">
              <Button variant="amber" size="lg" className="w-full h-20 text-xl">
                {bootcampStatus?.status === 'enrolling' ? 'ENROLL NOW →' : 'LEARN MORE →'}
              </Button>
            </Link>
            <p style={monoFont} className="text-center text-[9px] text-[#6B7280] mt-6 uppercase tracking-[0.2em]">Secure Checkout · 30-Day Refund</p>
          </motion.div>

          {/* 2. Mentorship Card */}
          <Card className="grid-card pos-mentorship order-2 p-10 flex flex-col gap-8">
            <div style={{ backgroundColor: '#F59E0B11', borderColor: '#F59E0B33' }} className="w-16 h-16 border flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-[#F59E0B]" />
            </div>
            <div>
              <h4 style={monoFont} className="text-2xl font-black text-white mb-4 uppercase tracking-tight">Live Mentorship</h4>
              <p style={soraFont} className="text-[#6B7280] text-sm leading-relaxed">
                Direct access to senior developers. Get code reviews and real-world insights in our interactive weekly sessions.
              </p>
            </div>
            <div className="mt-auto flex -space-x-4 items-center">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-12 h-12 rounded-none border-2 border-[#111827] bg-[#1F2937]" />
              ))}
              <div style={monoFont} className="pl-8 text-[9px] text-[#F59E0B] uppercase font-black tracking-widest">Active Community</div>
            </div>
          </Card>

          {/* 3. Small Cards */}
          {[
            { icon: Briefcase, t: "Real Projects", d: "Build production ML apps", color: "#00E676" },
            { icon: Award, t: "Certification", d: "Industry credentials", color: "#F59E0B" }
          ].map((item, i) => (
            <Card 
              key={i} 
              className={`grid-card order-3 p-8 flex items-center gap-6 hover:bg-white/5 transition-colors ${i === 0 ? 'pos-projects' : 'pos-cert'}`}
            >
              <div style={{ backgroundColor: `${item.color}11`, borderColor: `${item.color}33` }} className="w-14 h-14 border flex items-center justify-center shrink-0">
                <item.icon style={{ color: item.color }} className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 style={monoFont} className="text-sm font-black text-white truncate uppercase tracking-widest">{item.t}</h4>
                <p style={soraFont} className="text-[#6B7280] text-[10px] truncate">{item.d}</p>
              </div>
            </Card>
          ))}

          {/* 6. Curriculum Card */}
          <Card className="grid-card pos-curriculum order-4 p-10 flex flex-col gap-8">
            <div style={{ backgroundColor: '#00E67611', borderColor: '#00E67633' }} className="w-16 h-16 border flex items-center justify-center">
              <Code className="w-8 h-8 text-[#00E676]" />
            </div>
            <h4 style={monoFont} className="text-2xl font-black text-white uppercase tracking-tighter">Curriculum</h4>
            <div className="space-y-5">
              {[
                { w: "1-3", t: "Python Core" },
                { w: "4-6", t: "Data Science" },
                { w: "7-9", t: "Machine Learning" },
                { w: "10-12", t: "Deep Learning" }
              ].map((item, i) => (
                <div key={i} style={monoFont} className="flex items-center justify-between text-[11px] font-black uppercase py-2 border-b border-white/5 tracking-tight">
                  <span className="text-[#6B7280]">Week {item.w}</span>
                  <span className="text-white">{item.t}</span>
                </div>
              ))}
            </div>
          </Card>

        </div>
      </div>
    </section>
  );
};

export default BootcampSection;
